// Factory + auto-discovery for probe adapters.
// Callers should prefer `createProbeAdapter(vendor)` when the vendor is known
// (e.g. after a paired probe is stored in `probes` table), and `autoDiscover()`
// on the add-probe flow when the user taps "Cerca sonde".

import type { BleProbeAdapter, DetectedProbe, ProbeVendor } from '@trackeat/shared';
import { InkbirdAdapter } from './inkbird.js';
import { RuuviAdapter } from './ruuvi.js';
import { GoveeAdapter } from './govee.js';
import { XsenseCloudAdapter, type XsenseCloudConfig } from './xsense.js';
import { GenericBleProbeAdapter } from './generic-ble.js';

export interface ProbeAdapterConfig {
  xsense?: XsenseCloudConfig;
}

export function createProbeAdapter(
  vendor: ProbeVendor,
  cfg: ProbeAdapterConfig = {},
): BleProbeAdapter {
  switch (vendor) {
    case 'inkbird':
      return new InkbirdAdapter();
    case 'ruuvi':
      return new RuuviAdapter();
    case 'govee':
      return new GoveeAdapter();
    case 'xsense':
      if (!cfg.xsense) throw new Error('xsense_config_missing');
      return new XsenseCloudAdapter(cfg.xsense);
    case 'sensorpush':
    case 'dragino':
    case 'sonoff':
    case 'testo':
    case 'generic':
      // TODO: implement dedicated adapters; for now fall through.
      throw new Error(`adapter_not_implemented: ${vendor}`);
    default: {
      const exhaustive: never = vendor;
      throw new Error(`adapter_unknown: ${exhaustive as string}`);
    }
  }
}

/**
 * Run all BLE scans in parallel and tag every detected probe with its vendor.
 * Cloud-only adapters (e.g. xsense) are skipped here: they are handled from the
 * dedicated settings screen where the user provides credentials.
 */
export async function autoDiscover(timeoutMs = 8_000): Promise<DetectedProbe[]> {
  const bleAdapters: GenericBleProbeAdapter[] = [
    new InkbirdAdapter(),
    new RuuviAdapter(),
    new GoveeAdapter(),
  ];
  const results = await Promise.allSettled(
    bleAdapters.map((a) => a.scan(timeoutMs)),
  );
  const merged = new Map<string, DetectedProbe>();
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const probe of r.value) {
      // Vendor comes from the adapter that produced the DetectedProbe, so we
      // don't need a fallback here — keep the first hit per device_id.
      if (!merged.has(probe.device_id)) merged.set(probe.device_id, probe);
    }
  }
  return Array.from(merged.values());
}

export { GenericBleProbeAdapter } from './generic-ble.js';
export { InkbirdAdapter } from './inkbird.js';
export { RuuviAdapter } from './ruuvi.js';
export { GoveeAdapter } from './govee.js';
export { XsenseCloudAdapter } from './xsense.js';
